import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useSession } from '../context/SessionContext';
import '../styles/gather.css';



/**
 * Expected question shape (dummy/backend):
 * {
 *   question_id: "biz.001",
 *   pillar: "Business",
 *   text: "What types of products will you sell?",
 *   type: "single_select" | "multi_select" | "text" | "textarea" | "number",
 *   options?: ["Physical goods","Digital","Services"],
 *   unit?: "orders/day",
 *   placeholder?: "e.g., ..."
 * }
 */

const Gather = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { session, currentQuestion, loadSession, submitAnswer, loading, error } = useSession();
  
  const [currentAnswers, setCurrentAnswers] = useState({}); // Local form state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Get progress and current question based on index
  const progress = useMemo(() => {
    const totalQuestions = session?.questions?.length || 0;
    const answeredCount = session?.questions?.filter(q => q.answered)?.length || 0;
    return {
      answered: answeredCount,
      total_estimate: totalQuestions,
      current_index: currentQuestionIndex
    };
  }, [session?.questions, currentQuestionIndex]);

  // Get current question by index instead of first unanswered
  const currentQuestionByIndex = useMemo(() => {
    return session?.questions?.[currentQuestionIndex] || null;
  }, [session?.questions, currentQuestionIndex]);

  // Load session on mount
  useEffect(() => {
    console.log('🔍 Gather useEffect:', { sessionId, session: session?.sessionId, hasSession: !!session });
    if (sessionId && (!session || session.sessionId !== sessionId)) {
      console.log('📥 Loading session:', sessionId);
      loadSession(sessionId);
    }
  }, [sessionId, session, loadSession]);

  // Set initial question index to first unanswered question
  useEffect(() => {
    if (session?.questions) {
      const firstUnanswered = session.questions.findIndex(q => !q.answered);
      if (firstUnanswered >= 0) {
        setCurrentQuestionIndex(firstUnanswered);
      }
    }
  }, [session?.questions]);

  // Debug logging
  useEffect(() => {
    console.log('🎯 Current session state:', {
      session: session?.sessionId,
      questions: session?.questions?.length,
      currentQuestion: currentQuestion?.id,
      progress
    });
  }, [session, currentQuestion, progress]);

  // Controlled updates for current question
  const updateAnswer = (value) => {
    setCurrentAnswers({ [currentQuestionByIndex?.id]: value });
  };



  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setCurrentAnswers({});
    }
  };

  const handleNext = () => {
    const totalQuestions = session?.questions?.length || 0;
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setCurrentAnswers({});
    }
  };

  const handleSubmit = () => {
    navigate(`/project/${sessionId}/review`);
  };

  // Save answer when user makes a selection
  const saveAnswer = async (value) => {
    if (!currentQuestionByIndex) return;
    
    try {
      await submitAnswer(currentQuestionByIndex.id, value);
    } catch (e) {
      console.error('Failed to save answer:', e);
    }
  };

  // Renderer for current question (excluding free-form text)
  const renderInput = () => {
    if (!currentQuestionByIndex) return null;
    
    const q = currentQuestionByIndex;
    const v = currentAnswers[q.id] || q.answer;

    // Skip text/textarea questions for now
    if (!q.options || q.options.length === 0) {
      return (
        <div className="question-skipped">
          <p>Free-form questions are temporarily disabled.</p>
        </div>
      );
    }

    // Handle multi-select questions (checkboxes)
    if (q.type === 'multi-select') {
      const selectedValues = Array.isArray(v) ? v : [];
      const maxReached = q.maxSelections && selectedValues.length >= q.maxSelections;
      
      return (
        <div className="options-list">
          {q.maxSelections && (
            <div className="selection-counter">
              Selected: {selectedValues.length}/{q.maxSelections}
            </div>
          )}
          {q.options.map((option) => {
            const isSelected = selectedValues.includes(option);
            const isDisabled = maxReached && !isSelected;
            
            return (
              <label key={option} className={`option-item ${option === 'N/A' ? 'na-option' : ''} ${isDisabled ? 'disabled' : ''}`}>
                <input
                  type="checkbox"
                  value={option}
                  checked={isSelected}
                  disabled={isDisabled}
                  onChange={(e) => {
                    const newValues = isSelected 
                      ? selectedValues.filter(val => val !== option)
                      : [...selectedValues, option];
                    updateAnswer(newValues);
                    saveAnswer(newValues);
                  }}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
      );
    }

    // Handle single-select questions (radio buttons)
    return (
      <div className="options-list">
        {q.options.map((option) => (
          <label key={option} className={`option-item ${option === 'N/A' ? 'na-option' : ''}`}>
            <input
              type="radio"
              name={q.id}
              value={option}
              checked={v === option}
              onChange={(e) => {
                updateAnswer(e.target.value);
                saveAnswer(e.target.value);
              }}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="container service-page">
        <div className="page-heading">
          <h1>Guided Interview</h1>
          <p>Answer a few targeted questions so we can generate the right architecture artifacts.</p>
        </div>

        <div className="service-content">
          <div className="gather-header">
            <div className="progress">
              <strong>Progress:</strong>{' '}
              {progress.answered}/{progress.total_estimate} answered
            </div>
            <div className="session-id">
              <small>Session: {sessionId}</small>
            </div>
          </div>

          {error && <div className="error-banner">{error}</div>}

          {loading ? (
            <div className="loading">Loading session…</div>
          ) : currentQuestionByIndex ? (
            <div className="question-container">
              <div className="question-card">
                <div className="question-meta">
                  <span className="category">{currentQuestionByIndex.category}</span>
                  <span className="question-number">
                    Question {currentQuestionIndex + 1} of {progress.total_estimate}
                  </span>
                </div>
                <div className="question-text">{currentQuestionByIndex.text}</div>
                {currentQuestionByIndex.explanation && (
                  <div className="question-explanation">
                    <div className="explanation-icon">💡</div>
                    <div className="explanation-text">{currentQuestionByIndex.explanation}</div>
                  </div>
                )}
                <div className="question-input">
                  {renderInput()}
                </div>
              </div>
              <div className="actions">
                {(() => {
                  const isLastQuestion = currentQuestionIndex >= (session?.questions?.length || 0) - 1;
                  const isFirstQuestion = currentQuestionIndex === 0;
                  
                  return (
                    <>
                      <button
                        className={isFirstQuestion ? 'button-disabled' : 'button-enabled'}
                        onClick={isFirstQuestion ? undefined : handlePrevious}
                        disabled={isFirstQuestion}
                      >
                        Previous
                      </button>
                      
                      {isLastQuestion ? (
                        <button
                          className="button-enabled"
                          onClick={handleSubmit}
                        >
                          Submit Questions
                        </button>
                      ) : (
                        <button
                          className="button-enabled"
                          onClick={handleNext}
                        >
                          Next
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="no-questions">
              <p>All questions completed! ({progress.total_estimate}/{progress.total_estimate})</p>
              <button
                className="cta-button"
                onClick={() => navigate(`/project/${sessionId}/review`)}
              >
                Continue to Review
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Gather;